'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useModal } from 'components/shared/Modal';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  HERO_ROUTES,
  isHeroCategory,
  isHeroVerificationRegion,
  type HeroVerifyFailure,
} from 'lib/commerce/heroes/heroVerification';
import { HeroVerifyModal, type HeroVerifyError } from '../HeroVerifyModal';

// Tabs, carousels and reviews: a separate chunk, prefetched on idle. The modal
// shrink-wraps its child, so it only opens once the chunk has loaded.
let categoriesChunk: Promise<typeof import('../HeroCategoriesModal')> | null =
  null;
const loadCategories = () =>
  (categoriesChunk ??= import('../HeroCategoriesModal').catch((e) => {
    categoriesChunk = null;
    throw e;
  }));

const currentPath = () =>
  `${window.location.pathname}${window.location.search}`;

const FAILURES: HeroVerifyFailure[] = ['denied', 'cancelled', 'error'];
const asFailure = (v: string | null): HeroVerifyFailure | undefined =>
  FAILURES.find((f) => f === v);

/** Opens the US GovX verification modal on the current page. */
export function useHeroVerify() {
  const modal = useModal();
  const { region } = useRegionLocale();
  const enabled = isHeroVerificationRegion(region);

  const open = useCallback(
    (opts: { initialError?: HeroVerifyError } = {}) => {
      // May replace the categories modal.
      modal.hide();
      modal.set(
        <HeroVerifyModal
          key={Date.now()}
          returnTo={currentPath()}
          initialError={opts.initialError}
        />,
      );
      modal.show();
    },
    [modal],
  );

  return { enabled, open };
}

/**
 * Buy pages, every region: the "Special pricing for heroes" chip opens the
 * /heroes tabbed section in place. US tabs verify with GovX / ID.me; other
 * regions keep the consent sheet.
 */
export function useHeroOfferVerify(): () => void {
  const modal = useModal();

  useEffect(() => {
    const prefetch = () => void loadCategories().catch(() => {});
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(prefetch, 2000);
    return () => clearTimeout(timer);
  }, []);

  const onOpen = useCallback(() => {
    void loadCategories().then(({ default: HeroCategoriesModal }) => {
      modal.set(<HeroCategoriesModal />);
      modal.setBg('');
      modal.show();
    });
  }, [modal]);
  return onOpen;
}

/**
 * Reopens the modal at the error step after a failed GovX round trip, then
 * strips the params.
 */
export function useHeroVerifyReturn() {
  const { enabled, open } = useHeroVerify();
  const handled = useRef(false);

  useEffect(() => {
    if (!enabled || handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const category = params.get('heroVerify');
    // Only GovX comes back to the site, and only for its categories.
    if (
      !isHeroCategory(category) ||
      !HERO_ROUTES[category].providers.includes('govx')
    )
      return;
    handled.current = true;

    const govx = asFailure(params.get('govx'));

    // Only the failure-signal params; heroCategory etc. never ride a failure URL.
    ['heroVerify', 'govx'].forEach((k) => params.delete(k));
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}`,
    );

    open({
      initialError: govx
        ? { category, provider: 'govx', reason: govx }
        : undefined,
    });
  }, [enabled, open]);
}
